(ns lemonpi.content-function.shared.validation
  (:require [clojure.set :as set]
            [clojure.string :as str]
            [lambdaisland.uri :as uri]
            [lemonpi.content-function.interface.macro :as macro]
            [lemonpi.schema.interface :as schema]
            [malli.core :as m]
            [malli.error :as me]
            [malli.util :as mu]
            [mate.core :as mc]))

;;
;; The Registry
;;

(def cache* (atom nil))

(defn- ensure-keys
  [x ks]
  (reduce (fn [acc k] (cond-> acc (not (contains? x k)) (assoc k nil))) x ks))

(defn- attr-or-schema->schema
  [attr-or-schema]
  (try (m/deref (m/schema attr-or-schema))
       (catch #?(:clj Exception, :cljs js/Error) _)))

(defn- cascade-properties
  [schema ks]
  (let [;; create a lookup table of path -> properties (just ks keys)
        path->props (->> (mu/subschemas schema)
                         (reduce
                          (fn [acc {:keys [path schema]}]
                            (let [props (-> schema (m/properties) (select-keys ks))]
                              (cond-> acc (seq props) (assoc (seq path) props))))
                          {}))
        ;; reducer to accumulate selected properties from root
        ->props (fn [acc path]
                  (let [acc' (merge (path->props path) acc)]
                    (if (seq path)
                      (recur acc' (butlast path))
                      (ensure-keys acc' ks))))]
    (m/walk
     schema
     (fn [schema path children _]
       (m/-set-children (mu/update-properties schema ->props path) children)))))

(defn cached
  "Creates a schema instance and explainer for it. Returns cached results"
  [?schema]
  (let [create (fn []
                 (when-let [schema (some-> (attr-or-schema->schema ?schema)
                                           (cascade-properties [:stage :code]))]
                   {:schema schema
                    :explainer (m/explainer schema)}))]
    ;; don't register schemas ast
    (if (keyword? ?schema)
      (or (get @cache* ?schema)
          (when-let [item (create)]
            (swap! cache* assoc ?schema item)
            item))
      (create))))

(def ^:private schema-api-schema
  [:map
   [:meta [:map
           [:schema :string]
           [:id :string]]]
   [:data [:map [:out [:maybe :map]]]]])

(def error-stages
  #{:save-blocking :publish-blocking :warning})

(schema/register!
  :validation/result-schema
  [:map
   [:entity-values {:optional true}
    [:sequential
     [:map
      [:entity-value/name keyword?]
      [:entity-value/data any?]]]]
   [:path vector?]
   [:value any?]
   [:message string?]
   [:stage (into [:enum] error-stages)]
   [:code [:maybe keyword?]]])

(schema/register!
  :advertiser/url-whitelist
  [:maybe
   [:or
    [:set string?]
    [:sequential string?]]])

(defn- cphe?
  [m]
  (contains? m :collection-placeholder-expression/item))

(defn- sphe?
  [m]
  (contains? m :singular-placeholder-expression/strings))

(defn find-entity-data
  "Find entity data by entity name in validation result."
  [validation-result entity-name]
  {:pre [(m/validate :validation/result-schema validation-result)
         (keyword? entity-name)]}
  (some #(when (= (:entity-value/name %) entity-name)
           (:entity-value/data %))
        (:entity-values validation-result)))

(defn ->error->entity-values
  "Higher order function; for get a collection of entity values where
   the key matches the schema-property value `:entity` and the 
   value as partial `value` which matches the location where `:entity`
   is found within the schema.
 
   Example:
 
   ```clojure
   (def schema 
   [:map 
     {:entity :rule} ;; <= capture data as entity 
     [:rule/name string?]])    
   
   (def value {:rule/name 2})
 
   ;; returns a function which returns a collection of 
   ;; entity values for given error
   (def error->entity-values (->error->entity-values schema value))
 
   (map error->entity-values  (:errors (malli/explain schema value)))     
   ;; => [[#:entity-value {:rule {:rule/name 2}}]]
   ```"
  [schema value]
  {:pre [(m/schema? schema)]}
  (letfn [(get-entity-value
            [schema* path]
            (when-let [e (:entity (m/properties schema*))]
              (let [data (get-in value (mu/path->in schema path))]
                #:entity-value {:name e :data data})))]
    (fn [error]
      (loop [path (:path error) acc []]
        (let [next-path (if (seq path) (pop path) path)
              {:entity-value/keys [name data] :as ev}
              (get-entity-value (mu/get-in schema next-path) next-path)
              acc (cond-> acc
                    (and name data) (conj ev))]
          (if (empty? next-path)
            acc
            (recur next-path acc)))))))

(defn- add-property-should-be-unique?
  [schema error-message-subject]
  (mu/update-properties schema
                        merge
                        {:should-be-unique? true
                         :should-be-unique?/error-message (str (str/capitalize error-message-subject)
                                                               " should be unique.")}))

(defn- name-schema
  "`error-message-subject` is an articulated noun, e.g. \"a rule name\"."
  [error-message-subject min-chars max-chars]
  (let [subject (str/capitalize error-message-subject)]
    [:and
     [:string {:code :character-limit
               :error/message (str subject
                                   " should be between "
                                   min-chars
                                   " and "
                                   max-chars
                                   " characters long.")
               :min min-chars :max max-chars}]
     [:re {:code :allowed-characters
           :error/message (str subject " should only contain letters, digits , '-' and '_'.")}
      #"^[\w-]+$"]]))

(defn sequential-with-unique-check
  "Create a sequential schema of `schema-id` where each entry must be unique
  according to the `unique-attribute`.
  If the entry is not unique, show `duplicate-error-message`."
  [schema-id unique-attribute duplicate-error-message
   & {:keys [sequential-properties duplicate-check-properties]
      :or {sequential-properties {}}}]
  [:and
   {:entity schema-id}
   [:sequential sequential-properties schema-id]
   [:fn
    (merge
     {:error/message duplicate-error-message
      ;; TODO: check for better solution for :error/path based on generic
      ;; solution for https://github.com/metosin/malli/issues/483
      :error/path [0 unique-attribute]
      :code (keyword "unique" (name schema-id))
      :stage :save-blocking}
     duplicate-check-properties)
    (fn [items]
      (or (empty? items)
          (apply distinct? (map unique-attribute items))))]])

(defn ^:private create-name-schema
  [subject & {:keys [min-chars max-chars] :or {min-chars 1 max-chars 70}}]
  (-> (name-schema subject min-chars max-chars)
      (mu/update-properties assoc :stage :save-blocking)
      (add-property-should-be-unique? subject)))

(def ^:private placeholder-name-schema
  (create-name-schema "a placeholder name"))

(def ^:private rule-name-schema
  (create-name-schema "a rule name"))

(def ^:private context-source-alias-schema
  (create-name-schema "an alias"))

(def url-schema
  (m/schema
   [:re
    {:error/message "Invalid url."}
    "^(?:https?:)?//.+"]))

(defn singular-placeholder-expression->macro-expression
  [{:singular-placeholder-expression/keys [macro strings starts-with-macro?]}]
  (letfn [(pad [c] (concat c [nil]))]
    (remove nil?
            (if starts-with-macro?
              (interleave macro (pad strings))
              (interleave strings (pad macro))))))

(defn singular-placeholder-expression->string
  "Calculate user-visible/user-editable singular placeholder expression value (string)."
  [sphe]
  {:post [string?]}
  (macro/serialize-macro-expr (singular-placeholder-expression->macro-expression sphe)))

(def product-selector-id-error
  {:code :invalid-product-selector
   :stage :publish-blocking
   :error/fn (fn [{psid :value} _]
               (if (nil? psid)
                 "No product selector is selected"
                 "Invalid product selector reference"))})

(def feed-id-error
  {:code :invalid-feed-reference
   :stage :save-blocking
   :error/fn (fn [{fid :value} _]
               (if (nil? fid)
                 "Selecting a source feed is required"
                 "Invalid feed reference"))})

(def feed-lookup-id-error
  {:code :invalid-feed-lookup-id
   :stage :save-blocking
   :error/fn (fn [{fid :value} _]
               (if (nil? fid)
                 "Missing lookup id"
                 "Invalid lookup id"))})

(def feed-lookup-macro-error
  {:code :invalid-lookup-macro
   :stage :publish-blocking
   :error/fn (fn [{macro :value} _]
               (if (some? macro)
                 "Invalid macro"
                 "In order to lookup a record from the feed a dynamic value needs to be defined"))})

(schema/register!
  {:schema/selector [:cat [:* [:alt :keyword nat-int?]]]
   :context-source/alias context-source-alias-schema
   :context-source/schema schema-api-schema

   ;; dsp signal context source
   :dsp-signal/name
   (create-name-schema "a signal name" :max-chars 16)

   :dsp-signal/dsp-macro
   [:and
    [:string {:stage :warning
              :code :empty-value
              :error/message "A DSP macro cannot be empty" :min 1}]
    [:re {:code :allowed-characters
          :stage :warning
          :error/message "A DSP macro cannot contain '&' characters or spaces"}
     #"^[^&\s]+$|^$"]]

   :dsp-signal
   [:map
    :dsp-signal/name
    :dsp-signal/dsp-macro]

   :dsp-signal-context-source/signal
   (sequential-with-unique-check
    :dsp-signal
    :dsp-signal/name
    "A signal name can only be used one time")

   :dsp-signal-context-source
   [:map [:dsp-signal-context-source/signal {:optional true}]]

   ;; products context source
   :products-context-source/product-selector-id
   [:string (merge {:min 1} product-selector-id-error)]

   :products-context-source
   [:map
    product-selector-id-error
    :products-context-source/product-selector-id]

   ;; feed context source
   :feed-context-source/feed-id
   [nat-int? feed-id-error]

   :feed-context-source-lookup/id
   [:string (merge {:min 1} feed-lookup-id-error)]

   :feed-context-source-lookup/macro
   [:map feed-lookup-macro-error
    [:context-source/_macro [:map :context-source/alias]]
    [:macro/selector :schema/selector]]

   :feed-context-source-lookup
   [:map
    [:feed-context-source-lookup/id feed-lookup-id-error]
    [:feed-context-source-lookup/macro feed-lookup-macro-error]]

   :feed-context-source/lookup
   [:and
    (sequential-with-unique-check
     :feed-context-source-lookup
     :feed-context-source-lookup/id
     "A lookup can only be used one time")
    [:fn
     {:stage :warning
      :code :no-lookups-defined
      :error/message "In order to return a record from your feed a lookup needs to be defined"}
     not-empty]]

   :feed-context-source
   [:map
    [:feed-context-source/feed-id feed-id-error]
    :feed-context-source/lookup]

   :context-source
   [:and
    {:entity :context-source}
    [:map
     :context-source/alias
     [:context-source/schema {:optional true}]]
    [:multi
     {:dispatch #(get-in % [:context-source/schema :meta :id])
      :stage :save-blocking}
     [::m/default [:map]]
     ["urn:lemonpi:schema:context:dsp-signal:v1" :dsp-signal-context-source]
     ["urn:lemonpi:schema:context:products:v1" :products-context-source]
     ["urn:lemonpi:schema:context:content-variant:v1" :feed-context-source]]]

   :macro
   [:map
    {:error/message "Invalid macro."}
    [:context-source/_macro :context-source]
    [:macro/selector {:optional true} :schema/selector]
    [:macro/function {:optional true} [:cat :string [:* [:alt nat-int? :string :boolean]]]]]})

(def ^:private schema-sphe-starts-with-url
  (m/schema
   [:map
    {:stage :publish-blocking
     :code :starts-with-url
     :error/message "A placeholder expression should start with a valid url."}
    [:singular-placeholder-expression/starts-with-macro? {:optional true} [:= false]]
    [:singular-placeholder-expression/strings [:cat url-schema [:* :string]]]]))

(def ^:private schema-sphe-media-type
  (m/schema
   [:or
    {:code :starts-with-url-or-macro
     :stage :publish-blocking
     :error/message "A placeholder expression should be a single url or a single macro."}
    [:map
     [:singular-placeholder-expression/starts-with-macro? {:optional true} [:= false]]
     [:singular-placeholder-expression/strings [:cat [:alt url-schema [:= ""]]]]
     [:singular-placeholder-expression/macro {:optional true} empty?]]
    [:map
     [:singular-placeholder-expression/starts-with-macro? [:= true]]
     [:singular-placeholder-expression/strings empty?]
     [:singular-placeholder-expression/macro [:cat :macro]]]]))

(defn- sphe-max-chars-schema
  [max-chars]
  [:fn
   {:code :max-chars
    :error/message (str "A placeholder expression should be at most "
                        max-chars
                        " characters long.")}
   (fn [sphe]
     (<= (count (singular-placeholder-expression->string sphe))
         max-chars))])

(def ^:private predicate-schema
  [:map
   {:entity :predicate}
   :macro/selector])

(defn get-cleaned-up-host
  "Extracts the host part of a given URL, and removes any potential \\* and \\. characters from its left side."
  [url]
  (let [host (-> (cond->> url
                   ;; Prefixes a URL scheme if one is not already present, to help the parsing of the URL.
                   (and (not (str/starts-with? url "http://"))
                        (not (str/starts-with? url "https://")))
                   (str "scheme://"))
                 uri/uri
                 :host)
        [_ cleaned-up-host] (re-matches #"[\*\.]*(.*)" host)]
    cleaned-up-host))

(defn is-url-advertiser-whitelisted? [{whitelist-urls :advertiser/url-whitelist
                                       :singular-placeholder-expression/keys [strings starts-with-macro?]}]
  (or (empty? whitelist-urls)
      starts-with-macro?
      (let [whitelist-hosts (mapv get-cleaned-up-host whitelist-urls)
            url-host (-> (apply str strings) get-cleaned-up-host)]
        (-> (some (fn [whitelist-host]
                    (or (= url-host whitelist-host)
                        (str/ends-with? url-host (str "." whitelist-host))))
                  whitelist-hosts)
            boolean))))

(def ^:private singular-placeholder-expression-schema
  [:and
   {:entity :singular-placeholder-expression}

   [:map
    {:stage :save-blocking
     :code :invalid-format
     :error/message "Invalid placeholder expression format."}
    [:singular-placeholder-expression/strings [:sequential :string]]
    [:singular-placeholder-expression/macro {:optional true} [:sequential :macro]]
    [:singular-placeholder-expression/starts-with-macro? {:optional true} :boolean]]
   [:fn
    {:stage :save-blocking
     :code :missing-initial-macro
     :error/message "Missing initial macro."}
    (fn [{:singular-placeholder-expression/keys [macro starts-with-macro?]}]
      (or (not starts-with-macro?)
          (and starts-with-macro? (not-empty macro))))]

   ;; can not use macro-expressions within default placeholders
   [:fn {:code :macro-expression-in-default-placeholder
         :error/message "Default placeholder should not contain macro expressions."
         :stage :warning}
    (fn [{macro :singular-placeholder-expression/macro
          default-value :placeholder/_default-value}]
      (or (nil? default-value) (empty? macro)))]

   ;; used macro selectors must be present in schema
   [:map
    [:singular-placeholder-expression/macro {:optional true}
     (letfn [(macro-selector-valid?
               [{selector :macro/selector {{{selectors :out} :data} :context-source/schema} :context-source/_macro}]
               (let [selectors (set (keys selectors))]
                 (or (empty? selectors) ; fallback in case the selectors are unknown
                     (contains? selectors (first selector)))))

             (list-invalid-selectors
               [macros]
               (sequence
                (comp (remove macro-selector-valid?)
                      (map :macro/selector)
                      (map first)
                      (map name))
                macros))]
       [:fn
        {:code :macro-selector-not-in-schema
         :stage :warning
         :error/fn (fn [{macros :value} _]
                     (let [invalid-serialized-macro-expressions (list-invalid-selectors macros)
                           plural? (> (count invalid-serialized-macro-expressions) 1)]
                       (str "Macro" (when plural? "s")
                            " with unavailable selector: "
                            (clojure.string/join ", " invalid-serialized-macro-expressions))))}
        (fn [macros]
          (or (empty? macros)
              (empty? (list-invalid-selectors macros))))])]]

   [:multi
    {:dispatch (comp :placeholder/type (some-fn :placeholder/_override
                                                :placeholder/_default-value))
     :stage :save-blocking
     :error/fn (fn [{e :type} _]
                 (when (= e :malli.core/invalid-dispatch-value)
                   "Unknown placeholder type."))}
    [:text
     (sphe-max-chars-schema 10000)]
    [:audio
     [:and
      (sphe-max-chars-schema 2048)
      schema-sphe-media-type]]
    [:image
     [:and
      (sphe-max-chars-schema 2048)
      schema-sphe-media-type]]
    [:video
     [:and
      (sphe-max-chars-schema 2048)
      schema-sphe-media-type]]
    [:click
     [:and
      (sphe-max-chars-schema 2048)

      ;; value should be whitelisted
      [:map :advertiser/url-whitelist]
      [:fn
       {:stage :warning
        :code :url-not-whitelisted
        :error/path [:singular-placeholder-expression/strings]
        :error/message "The url should be whitelisted"}
       is-url-advertiser-whitelisted?]

      ;; should start with url or macro
      [:or
       {:stage :publish-blocking
        :code :starts-with-url-or-macro
        :error/message "A placeholder expression should start with a valid url or a macro."}
       schema-sphe-starts-with-url
       [:map
        [:singular-placeholder-expression/starts-with-macro? [:= true]]
        [:singular-placeholder-expression/macro [:cat [:+ :macro]]]]]]]

    [:impression-tracker
     [:and
      (sphe-max-chars-schema 2048)
      schema-sphe-starts-with-url]]
    [:click-tracker
     [:and
      (sphe-max-chars-schema 2048)
      schema-sphe-starts-with-url
      [:map
       [:singular-placeholder-expression/strings
        [:fn
         {:stage :publish-blocking
          :code :click-tracker-landing-url
          :error/message "A placeholder expression should contain '(:landing_url:)' or '(:landing_url_enc:)'."}
         (fn [strings]
           (some #(re-matches #".*\(:landing_url(_enc)?:\).*" %)
                 strings))]]]]]]])

(def ^:private collection-placeholder-expression-schema
  [:map {:entity :collection-placeholder-expression}
   [:collection-placeholder-expression/item
    [:sequential
     [:map
      [:collection-item/override
       [:sequential singular-placeholder-expression-schema]]]]]])

(def ^:private placeholder-expression-schema
  [:multi
   {:dispatch #(cond (cphe? %) :collection
                     (sphe? %) :singular)}
   [:collection collection-placeholder-expression-schema]
   [:singular singular-placeholder-expression-schema]])

(schema/register!
  {:placeholder/name placeholder-name-schema
   :rule/name rule-name-schema

   :placeholder
   [:map
    {:entity :placeholder}
    [:placeholder/name placeholder-name-schema]
    [:placeholder/default-value {:optional true}
     placeholder-expression-schema]
    [:placeholder/override {:optional true}
     [:sequential placeholder-expression-schema]]
    [:placeholder/type keyword?]]

   :rule/assignment [:sequential placeholder-expression-schema]
   :rule/condition [:sequential predicate-schema]

   :rule
   [:and
    {:entity :rule}
    [:map
     [:rule/name rule-name-schema]
     [:mapped-rule/has-mapped-targeting-value? {:optional true} boolean?]
     [:rule/condition {:optional true} [:sequential predicate-schema]]
     [:rule/assignment {:optional true} [:sequential placeholder-expression-schema]]]
    [:fn
     {:code :default-targeting-value-non-existing
      :stage :warning
      :error/message "The targeting value is set as default, but is missing from the feed."}
     (fn [{:mapped-rule/keys [has-targeting-value?] [first-assignment & _] :rule/assignment}]
       ;; if the first assignment is a default placeholder expression and
       ;; `has-targeting-value` is `false` (but not nil) then an error will be
       ;; shown. In all other cases no error will be shown
       (not (and (some? (:placeholder/_default-value first-assignment))
                 (some? has-targeting-value?) ; for compatibility with non-mapped rules
                 (not has-targeting-value?))))]]

   :macro/selector [:* [:alt :keyword nat-int?]]

   :singular-placeholder-expression singular-placeholder-expression-schema
   :collection-placeholder-expression collection-placeholder-expression-schema
   :placeholder-expression placeholder-expression-schema

   :predicate predicate-schema})

(defn list-missing-required-placeholders
  [placeholders required-placeholders]
  (letfn [(make-set [p] (into #{} (map #(select-keys % [:placeholder/name :placeholder/type])) p))]
    (set/difference
     (make-set required-placeholders)
     (make-set placeholders))))

(schema/register!
  {:content-configuration/rule (sequential-with-unique-check
                                :rule :rule/name
                                "All rule names should be unique")
   :content-configuration/placeholder (sequential-with-unique-check
                                       :placeholder :placeholder/name
                                       "All placeholder names should be unique")
   :content-configuration/context-source (sequential-with-unique-check
                                          :context-source :context-source/alias
                                          "All context source aliases should be unique")
   :content-configuration
   [:and
    {:entity :content-configuration}
    [:map
     [:content-configuration/rule {:optional true}]
     [:content-configuration/placeholder {:optional true}]
     [:content-configuration/context-source {:optional true}]]
    [:fn {:error/message "Some required placeholders are missing"
          :code :missing-required-placeholder
          :stage :publish-blocking}
     (fn [cc]
       (mc/implies (and (= (:adset/workflow cc) "rules")
                        (contains? cc :content-configuration/placeholder))
                   (empty? (list-missing-required-placeholders
                            (:content-configuration/placeholder cc)
                            (get-in cc [:adset/_content-configuration :adset/assigned-template :template-revision/template-placeholder])))))]]})

(defn- error->validation-result
  [error]
  (merge
   {:value (:value error)
    :message (:message (me/with-error-message error))
    :path (me/error-path error)}
   (select-keys (m/properties (:schema error))
                [:stage :code])))

(defn root-error-messages
  "Given an explanation, return list of errors with the error of the root
  schema (if error message is set)."
  [{:keys [errors] :as explanation}]
  (map #(update % :schema
                mu/update-properties
                merge (nth
                       (me/-resolve-root-error explanation % nil)
                       2))
       errors))

(defn validate
  "Returns a sequence of validation results or nil.
   `attr-or-schema` is either a keyword representing an attribute
   or an actual schema such that the calls
   (validate :placeholder/name x) and
   (validate (:placeholder/name definitions) x)
   are equivalent. Returns nil if `x` is valid, otherwise a sequence of
   maps {:stage :some-stage :message \"some message\"}.
   If an unknown keyword is supplied, returns nil."
  [attr-or-schema x]
  (when-let [{:keys [schema explainer]} (cached attr-or-schema)]
    (when-let [errors (not-empty (root-error-messages (explainer x)))]
      (let [error->entity-values (->error->entity-values schema x)]
        (sequence (comp
                   (map #(-> (error->validation-result %)
                             (assoc :entity-values (error->entity-values %))))
                   (map #(assoc % :stage (or (:stage %) :save-blocking))))
                  errors)))))

(defn valid?
  [attr-or-schema x]
  (if-let [schema (attr-or-schema->schema attr-or-schema)]
    (m/validate schema x)
    true))

(defn should-be-unique?
  [attr-or-schema]
  (if-let [schema (attr-or-schema->schema attr-or-schema)]
    (:should-be-unique? (m/properties schema))
    false))

(defn duplicate-error-message
  [attr]
  (some-> attr
          attr-or-schema->schema
          (m/properties)
          (:should-be-unique?/error-message)))
